import React, { useState, useEffect, useRef } from 'react';
import { Upload, DollarSign, Calendar, User, AlertCircle, Search, CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import apiClient from '../api/client';

const XmlAnalyzer = () => {
  // Referencia a la combinación ctrl + enter para atajo de teclado
  const AtajoCalcular = useRef(null);

  // CFDIs cargados: [{...datos parseados, fileName}] — soporta factura global + complementos (p.ej. día 31)
  const [facturas, setFacturas] = useState([]);
  const [error, setError] = useState('');

  // Conciliación
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFin, setPeriodoFin] = useState('');
  const [conciliacion, setConciliacion] = useState(null);
  const [loadingConciliacion, setLoadingConciliacion] = useState(false);
  const [errorConciliacion, setErrorConciliacion] = useState('');
  const [expandedGrupos, setExpandedGrupos] = useState(new Set());
  const [manualLinks, setManualLinks] = useState(new Map()); // dbCasetaKey → cfdiGroupKey
  const [busquedasManuales, setBusquedasManuales] = useState(new Map()); // lineKey → { loading, candidatos, error }
  const [conciliacionesManuales, setConciliacionesManuales] = useState(new Map()); // lineKey → vínculo confirmado
  const [combinacionesManuales, setCombinacionesManuales] = useState(new Map()); // grupoKey → combinaciones confirmadas

  // Filtro de estado sobre la tabla de conciliación
  const [filtroEstado, setFiltroEstado] = useState('todas'); // 'todas' | 'diferencia' | 'sinMatch' | 'coincide'

  // Filas marcadas como revisadas/justificadas (en memoria de la sesión): key → { nota, fecha }
  const [revisadas, setRevisadas] = useState(new Map());
  const [notaEditKey, setNotaEditKey] = useState(null);
  const [notaBorrador, setNotaBorrador] = useState('');

  // Al cargar/quitar facturas, derivar el período del mes de la factura más antigua
  useEffect(() => {
    if (facturas.length > 0) {
      const masAntigua = facturas.reduce(
        (min, f) => (new Date(f.fecha) < new Date(min.fecha) ? f : min),
        facturas[0]
      );
      const d = new Date(masAntigua.fecha);
      const inicio = new Date(d.getFullYear(), d.getMonth(), 1);
      const fin    = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const fmt = (date) => date.toISOString().split('T')[0];
      setPeriodoInicio(fmt(inicio));
      setPeriodoFin(fmt(fin));
    }
    setConciliacion(null);
    setExpandedGrupos(new Set());
    setManualLinks(new Map());
    setBusquedasManuales(new Map());
    setConciliacionesManuales(new Map());
    setCombinacionesManuales(new Map());
    setErrorConciliacion('');
    setFiltroEstado('todas');
    setRevisadas(new Map());
    setNotaEditKey(null);
  }, [facturas]);

   useEffect(() => {
          document.addEventListener('keydown', handleKeyDown);
  
          // Función de limpieza que se ejecuta al desmontar el componente
          return () => {
              document.removeEventListener('keydown', handleKeyDown);
          };
      }, []);

  // ===== MANEJADOR DEL KEYDOWN =====
    const handleKeyDown = (event) => {
        // Verificar si se presionó Ctrl y Enter
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault();
            // Simular un clic en el botón referenciado
            if (AtajoCalcular.current) {
                AtajoCalcular.current.click();
            }
        }
    };

  const readFileAsText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.readAsText(file);
  });

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setError('');

    const nuevas = [];
    const errores = [];
    const uuidsCargados = new Set(facturas.map(f => f.uuid));

    for (const file of files) {
      try {
        const text = await readFileAsText(file);
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(text, "text/xml");
        if (xmlDoc.getElementsByTagName("parsererror").length > 0) {
          throw new Error("no es un XML válido");
        }
        const parsed = parseCFDI(xmlDoc);
        if (parsed.uuid && uuidsCargados.has(parsed.uuid)) {
          throw new Error(`factura duplicada (UUID ${parsed.uuid} ya cargado)`);
        }
        uuidsCargados.add(parsed.uuid);
        nuevas.push({ ...parsed, fileName: file.name });
      } catch (err) {
        errores.push(`${file.name}: ${err.message}`);
        console.error(err);
      }
    }

    if (nuevas.length > 0) setFacturas(prev => [...prev, ...nuevas]);
    if (errores.length > 0) setError(errores.join(' · '));
    event.target.value = ''; // permite volver a seleccionar el mismo archivo
  };

  const removeFactura = (index) => {
    setFacturas(prev => prev.filter((_, i) => i !== index));
  };

  const parseCFDI = (xmlDoc) => {
    const getTag = (tagName) => {
      const tags = xmlDoc.getElementsByTagName(tagName);
      if (tags.length === 0) {
        return xmlDoc.getElementsByTagName("cfdi:" + tagName);
      }
      return tags;
    };

    const comprobante = getTag("Comprobante")[0];
    const emisor = getTag("Emisor")[0];
    const receptor = getTag("Receptor")[0];
    const conceptos = getTag("Concepto");

    if (!comprobante) throw new Error("no es un comprobante CFDI válido");

    const conceptosArray = [];
    for (let i = 0; i < conceptos.length; i++) {
      const c = conceptos[i];
      conceptosArray.push({
        cantidad: c.getAttribute("Cantidad"),
        unidad: c.getAttribute("Unidad") || c.getAttribute("ClaveUnidad"),
        descripcion: c.getAttribute("Descripcion"),
        valorUnitario: parseFloat(c.getAttribute("ValorUnitario")),
        importe: parseFloat(c.getAttribute("Importe")),
        claveProdServ: c.getAttribute("ClaveProdServ")
      });
    }

    const timbre = getTag("TimbreFiscalDigital")[0] || {};

    return {
      serie: comprobante.getAttribute("Serie") || '',
      folio: comprobante.getAttribute("Folio") || '',
      fecha: comprobante.getAttribute("Fecha"),
      total: parseFloat(comprobante.getAttribute("Total")),
      subTotal: parseFloat(comprobante.getAttribute("SubTotal")),
      moneda: comprobante.getAttribute("Moneda"),
      tipoDeComprobante: comprobante.getAttribute("TipoDeComprobante"),
      emisor: {
        rfc: emisor?.getAttribute("Rfc"),
        nombre: emisor?.getAttribute("Nombre"),
        regimen: emisor?.getAttribute("RegimenFiscal")
      },
      receptor: {
        rfc: receptor?.getAttribute("Rfc"),
        nombre: receptor?.getAttribute("Nombre"),
        usoCFDI: receptor?.getAttribute("UsoCFDI")
      },
      conceptos: conceptosArray,
      uuid: timbre.getAttribute ? timbre.getAttribute("UUID") : null
    };
  };

  const formatearDinero = (cantidad) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(cantidad);
  };

  // Normaliza un string para comparación: quita "PEAJE ", acentos y caracteres especiales
  const normalizeStr = (str) => {
    if (!str) return '';
    return str
      .toUpperCase()
      .replace(/^PEAJE\s+/, '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^A-Z0-9 ]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handleConciliar = async () => {
    if (!periodoInicio || !periodoFin || facturas.length === 0) return;
    setLoadingConciliacion(true);
    setErrorConciliacion('');
    setConciliacion(null);
    setExpandedGrupos(new Set());
    setFiltroEstado('todas');
    setRevisadas(new Map());
    setNotaEditKey(null);
    setBusquedasManuales(new Map());
    setConciliacionesManuales(new Map());
    setCombinacionesManuales(new Map());
    try {
      const { data } = await apiClient.get('/api/cruces/conciliacion', {
        params: { fechaInicio: periodoInicio, fechaFin: periodoFin }
      });

      // ── 1. Construir mapa de casetas desde los cruces (ahora con desglose por Clase) ──
      // casetaMap: normalizedKey → { Caseta, idCaseta, totalCruces, totalImporte, clases[] }
      // Nota: el backend agrupa por (Caseta, idCaseta, Clase). Cuando el mismo nombre de caseta
      // tiene más de un idCaseta en el catálogo (duplicados de datos), llegan varias filas para
      // la misma Clase. Aquí las volvemos a mergear por Clase para que la UI muestre una sola
      // fila consistente por clase de vehículo, en vez de filas repetidas o casi-repetidas.
      const mergeClaseEntry = (clases, row) => {
        const claseLabel = row.Clase || 'Sin clase';
        const cruces  = Number(row.TotalCruces)  || 0;
        const importe = Number(row.TotalImporte) || 0;
        const existente = clases.find(c => c.Clase === claseLabel);
        if (existente) {
          existente.TotalCruces  += cruces;
          existente.TotalImporte += importe;
          existente.tarifaPromedio = existente.TotalCruces > 0
            ? existente.TotalImporte / existente.TotalCruces
            : 0;
        } else {
          clases.push({
            Clase:          claseLabel,
            TotalCruces:    cruces,
            TotalImporte:   importe,
            tarifaPromedio: cruces > 0 ? importe / cruces : 0,
          });
        }
      };

      const casetaMap = new Map();
      for (const row of data) {
        const key = normalizeStr(row.Caseta);
        const cruces  = Number(row.TotalCruces)  || 0;
        const importe = Number(row.TotalImporte) || 0;
        if (casetaMap.has(key)) {
          const e = casetaMap.get(key);
          e.totalCruces  += cruces;
          e.totalImporte += importe;
          mergeClaseEntry(e.clases, row);
        } else {
          const clases = [];
          mergeClaseEntry(clases, row);
          casetaMap.set(key, {
            Caseta:       row.Caseta,
            idCaseta:     row.idCaseta,
            totalCruces:  cruces,
            totalImporte: importe,
            clases,
          });
        }
      }
      // Orden estable de clases para lectura consistente (1, 2, 3… / alfabético si no son numéricas)
      for (const e of casetaMap.values()) {
        e.clases.sort((a, b) => {
          const na = Number(a.Clase), nb = Number(b.Clase);
          if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
          return String(a.Clase).localeCompare(String(b.Clase));
        });
      }

      // ── 2. Agrupar conceptos de TODAS las facturas cargadas por caseta normalizada ──
      // conceptosMap: normalizedKey → { lineasCFDI[], totalCantidad, totalImporte }
      const conceptosMap = new Map();
      for (const f of facturas) {
        const etiquetaFactura = [f.serie, f.folio].filter(Boolean).join('-') || f.fileName;
        for (const concepto of f.conceptos) {
          const key = normalizeStr(concepto.descripcion);
          const _importe      = Number(concepto.importe)       || 0;
          const _valorUnit    = Number(concepto.valorUnitario) || 0;
          const linea = {
            descripcion:         concepto.descripcion,
            factura:             etiquetaFactura,          // CFDI de origen (útil con varias facturas)
            cantidad:            Number(concepto.cantidad) || 0,
            importe:             _importe,
            importeConIVA:       _importe * 1.16,          // con IVA — mismo criterio que cruces.Importe
            valorUnitario:       _valorUnit,
            valorUnitarioConIVA: _valorUnit * 1.16,        // tarifa facturada por cruce, con IVA
            claveProdServ:       concepto.claveProdServ,
          };
          if (conceptosMap.has(key)) {
            const g = conceptosMap.get(key);
            g.lineasCFDI.push(linea);
            g.totalCantidad += linea.cantidad;
            g.totalImporte  += linea.importeConIVA;
          } else {
            conceptosMap.set(key, {
              key,
              lineasCFDI:    [linea],
              totalCantidad: linea.cantidad,
              totalImporte:  linea.importeConIVA,
            });
          }
        }
      }

      // ── 3. Cruzar cada grupo CFDI contra el mapa de cruces ──
      const grupos = [];
      const matchedCasetaKeys = new Set();

      for (const [key, cfdiGroup] of conceptosMap.entries()) {
        let match = casetaMap.get(key) || null;
        let matchedKey = casetaMap.has(key) ? key : null;
        // Fallback: coincidencia parcial
        if (!match) {
          for (const [dbKey, dbVal] of casetaMap.entries()) {
            if (key.includes(dbKey) || dbKey.includes(key)) {
              match = dbVal;
              matchedKey = dbKey;
              break;
            }
          }
        }
        if (matchedKey) matchedCasetaKeys.add(matchedKey);
        grupos.push({
          key,
          casetaDisplay:          match ? match.Caseta : key,
          lineasCFDI:             cfdiGroup.lineasCFDI,
          totalCantidadFacturada: cfdiGroup.totalCantidad,
          totalImporteFacturado:  cfdiGroup.totalImporte,
          match,
          diferencia:   match !== null ? cfdiGroup.totalImporte  - match.totalImporte : null,
          difCantidad:  match !== null ? cfdiGroup.totalCantidad - match.totalCruces  : null,
        });
      }

      // ── 4. Cruces del período sin match en el CFDI ──
      const sinFactura = [];
      for (const [dbKey, dbVal] of casetaMap.entries()) {
        if (!matchedCasetaKeys.has(dbKey)) sinFactura.push({ ...dbVal, _key: dbKey });
      }
      sinFactura.sort((a, b) => a.Caseta.localeCompare(b.Caseta));

      // Ordenar: primero con diferencia, luego sin coincidencia, resto alfabético
      grupos.sort((a, b) => {
        if (!a.match && b.match)  return  1;
        if (a.match  && !b.match) return -1;
        if (a.match && b.match) {
          const aDif = Math.abs(a.diferencia) > 0.01 ? 1 : 0;
          const bDif = Math.abs(b.diferencia) > 0.01 ? 1 : 0;
          if (aDif !== bDif) return bDif - aDif;
        }
        return a.key.localeCompare(b.key);
      });

      setConciliacion({ grupos, sinFactura });
    } catch (err) {
      setErrorConciliacion('Error al obtener los datos de cruces. Verifica la conexión con el servidor.');
      console.error(err);
    } finally {
      setLoadingConciliacion(false);
    }
  };

  const toggleGrupo = (key) => {
    setExpandedGrupos(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleLink = (dbKey, cfdiKey) => {
    setManualLinks(prev => {
      const next = new Map(prev);
      if (cfdiKey) next.set(dbKey, cfdiKey);
      else next.delete(dbKey);
      return next;
    });
  };

  const buscarCrucesFueraPeriodo = async (lineKey, grupo, linea) => {
    setBusquedasManuales(prev => new Map(prev).set(lineKey, { loading: true, candidatos: [], error: '' }));
    try {
      const fechaComoTexto = (fecha) => {
        const year = fecha.getFullYear();
        const month = String(fecha.getMonth() + 1).padStart(2, '0');
        const day = String(fecha.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };
      const inicioBusqueda = new Date(`${periodoInicio}T00:00:00`);
      inicioBusqueda.setDate(inicioBusqueda.getDate() - 5);
      const finBusqueda = new Date(`${periodoFin}T00:00:00`);
      finBusqueda.setDate(finBusqueda.getDate() + 5);
      const { data } = await apiClient.get('/api/cruces/conciliacion/manual', {
        params: {
          caseta: grupo.match?.Caseta || grupo.casetaDisplay,
          fechaInicio: periodoInicio,
          fechaFin: periodoFin,
          busquedaInicio: fechaComoTexto(inicioBusqueda),
          busquedaFin: fechaComoTexto(finBusqueda),
          cantidad: linea.cantidad,
          importe: linea.importeConIVA,
        },
      });
      setBusquedasManuales(prev => new Map(prev).set(lineKey, { loading: false, candidatos: data, error: '' }));
    } catch (err) {
      setBusquedasManuales(prev => new Map(prev).set(lineKey, {
        loading: false,
        candidatos: [],
        error: 'No se pudo buscar fuera del período conciliado.',
      }));
    }
  };

  const guardarConciliacionManual = (lineKey, grupo, linea, candidato) => {
    setConciliacionesManuales(prev => new Map(prev).set(lineKey, {
      grupoKey: grupo.key,
      caseta: grupo.match?.Caseta || grupo.casetaDisplay,
      lineaCFDI: {
        descripcion: linea.descripcion,
        cantidad: linea.cantidad,
        importeConIVA: linea.importeConIVA,
      },
      fechaInicioConciliada: periodoInicio,
      fechaFinConciliada: periodoFin,
      candidato: {
        ...candidato,
        cruceIds: candidato.CruceIds ? candidato.CruceIds.split(',') : [],
      },
      fechaVinculo: new Date().toISOString(),
    }));
  };

  const quitarConciliacionManual = (lineKey) => {
    setConciliacionesManuales(prev => {
      const next = new Map(prev);
      next.delete(lineKey);
      return next;
    });
  };

  const obtenerConciliacionManualGrupo = (grupoKey) => {
    const individuales = [...conciliacionesManuales.values()]
      .filter(vinculo => vinculo.grupoKey === grupoKey);
    const combinaciones = combinacionesManuales.get(grupoKey) || [];
    return {
      cantidad: individuales.reduce((total, vinculo) => total + Number(vinculo.candidato?.TotalCruces || 0), 0)
        + combinaciones.reduce((total, combinacion) => total + combinacion.cantidad, 0),
      importe: individuales.reduce((total, vinculo) => total + Number(vinculo.candidato?.TotalImporte || 0), 0)
        + combinaciones.reduce((total, combinacion) => total + combinacion.importe, 0),
    };
  };

  const detalleItemKey = (item) => [
    item.detalleId,
    item.descripcion || item.Clase,
    item.cantidad ?? item.TotalCruces,
    Math.round((item.importeConIVA ?? item.TotalImporte) * 100),
  ].join('|');

  const buscarCombinacionesExactas = (lineas, clases) => {
    if (lineas.length > 12 || clases.length > 12) return [];
    const obtenerSubconjuntos = (items, cantidadKey, importeKey) => {
      const subconjuntos = [];
      const recorrer = (indice, seleccion, cantidad, importe) => {
        if (indice === items.length) {
          if (seleccion.length > 0) subconjuntos.push({ seleccion, cantidad, importe });
          return;
        }
        recorrer(indice + 1, seleccion, cantidad, importe);
        const item = items[indice];
        recorrer(
          indice + 1,
          [...seleccion, item],
          cantidad + Number(item[cantidadKey] || 0),
          importe + Number(item[importeKey] || 0),
        );
      };
      recorrer(0, [], 0, 0);
      return subconjuntos;
    };

    const combinaciones = [];
    const porImporte = new Map();
    obtenerSubconjuntos(clases, 'TotalCruces', 'TotalImporte').forEach(subconjunto => {
      const clave = `${subconjunto.cantidad}|${Math.round(subconjunto.importe * 100)}`;
      const existentes = porImporte.get(clave) || [];
      existentes.push(subconjunto);
      porImporte.set(clave, existentes);
    });

    obtenerSubconjuntos(lineas, 'cantidad', 'importeConIVA').forEach(lineasSeleccionadas => {
      const clave = `${lineasSeleccionadas.cantidad}|${Math.round(lineasSeleccionadas.importe * 100)}`;
      const clasesSeleccionadas = porImporte.get(clave) || [];
      clasesSeleccionadas.forEach(clasesEncontradas => {
        combinaciones.push({
          lineas: lineasSeleccionadas.seleccion,
          clases: clasesEncontradas.seleccion,
          cantidad: lineasSeleccionadas.cantidad,
          importe: lineasSeleccionadas.importe,
        });
      });
    });
    return combinaciones;
  };

  const guardarCombinacionManual = (grupo, combinacion) => {
    setCombinacionesManuales(prev => {
      const next = new Map(prev);
      const existentes = next.get(grupo.key) || [];
      next.set(grupo.key, [...existentes, {
        grupoKey: grupo.key,
        caseta: grupo.match?.Caseta || grupo.casetaDisplay,
        lineasCFDI: combinacion.lineas.map(linea => ({
          detalleId: linea.detalleId,
          descripcion: linea.descripcion,
          cantidad: linea.cantidad,
          importeConIVA: linea.importeConIVA,
          factura: linea.factura,
        })),
        clasesIAVE: combinacion.clases.map(clase => ({
          detalleId: clase.detalleId,
          Clase: clase.Clase,
          TotalCruces: clase.TotalCruces,
          TotalImporte: clase.TotalImporte,
        })),
        cantidad: combinacion.cantidad,
        importe: combinacion.importe,
        fechaInicioConciliada: periodoInicio,
        fechaFinConciliada: periodoFin,
        fechaVinculo: new Date().toISOString(),
      }]);
      return next;
    });
  };

  const quitarCombinacionManual = (grupoKey, indice) => {
    setCombinacionesManuales(prev => {
      const next = new Map(prev);
      const restantes = (next.get(grupoKey) || []).filter((_, i) => i !== indice);
      if (restantes.length > 0) next.set(grupoKey, restantes);
      else next.delete(grupoKey);
      return next;
    });
  };

  // El estado se determina por el PORCENTAJE de la diferencia sobre lo facturado (no por el monto absoluto):
  // < 1% → "Dif. mínima" · ≥ 1% → "Diferencia". El % se muestra en el badge para transparencia.
  const getEstadoBadge = (diferencia, importeFacturado) => {
    const badgeStyle = { fontSize: '0.78rem' };
    if (diferencia === null)
      return <span className="badge badge-secondary" style={badgeStyle}><XCircle size={13} className="mr-1" />Sin coincidencia</span>;
    if (Math.abs(diferencia) < 0.01)
      return <span className="badge badge-success" style={badgeStyle}><CheckCircle size={13} className="mr-1" />Coincide</span>;
    const pct = importeFacturado > 0 ? (Math.abs(diferencia) / importeFacturado) * 100 : 100;
    const pctLabel = pct < 0.01 ? '<0.01%' : `${pct.toFixed(2)}%`;
    if (pct < 1)
      return (
        <span className="badge badge-warning" style={badgeStyle} title={`La diferencia representa ${pctLabel} del importe facturado (umbral: 1%)`}>
          <AlertTriangle size={13} className="mr-1" />Dif. mínima · {pctLabel}
        </span>
      );
    return (
      <span className="badge badge-danger" style={badgeStyle} title={`La diferencia representa ${pctLabel} del importe facturado (umbral: 1%)`}>
        <AlertTriangle size={13} className="mr-1" />Diferencia · {pctLabel}
      </span>
    );
  };

  // Clasifica una fila del resultado de conciliación en un estado simple para filtrar/exportar
  const rowEstado = (g) => {
    if (!g.match) return 'sinMatch';
    if (Math.abs(g.diferenciaVisual ?? g.diferencia) < 0.01) return 'coincide';
    return 'diferencia';
  };

  const filtrarGrupos = (grupos) => {
    if (filtroEstado === 'todas') return grupos;
    return grupos.filter(g => rowEstado(g) === filtroEstado);
  };

  // Empareja cada línea CFDI con una clase de cruces solo cuando cantidad e importe c/IVA son exactos.
  const emparejarDetalles = (lineasCFDI, clases) => {
    const lineasIdentificadas = lineasCFDI.map((linea, index) => ({ ...linea, detalleId: `linea-${index}` }));
    const clasesIdentificadas = clases.map((clase, index) => ({ ...clase, detalleId: `clase-${index}` }));
    const disponibles = clasesIdentificadas.map((clase, index) => ({ clase, index }));
    const coincidencias = [];
    const lineasSinCoincidencia = [];

    for (const linea of lineasIdentificadas) {
      const indice = disponibles.findIndex(({ clase }) => (
        linea.cantidad === clase.TotalCruces
        && Math.round(linea.importeConIVA * 100) === Math.round(clase.TotalImporte * 100)
      ));

      if (indice === -1) {
        lineasSinCoincidencia.push(linea);
        continue;
      }

      const [encontrada] = disponibles.splice(indice, 1);
      coincidencias.push({ linea, clase: encontrada.clase });
    }

    return {
      coincidencias,
      lineasSinCoincidencia,
      clasesSinCoincidencia: disponibles.map(({ clase }) => clase),
    };
  };

  const marcarRevisada = (key, nota) => {
    setRevisadas(prev => {
      const next = new Map(prev);
      next.set(key, { nota: nota || '', fecha: new Date().toISOString() });
      return next;
    });
    setNotaEditKey(null);
    setNotaBorrador('');
  };

  const quitarRevisada = (key) => {
    setRevisadas(prev => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  };

  const csvEscape = (val) => {
    const s = String(val ?? '');
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportarConciliacionCSV = (grupos) => {
    const filas = filtrarGrupos(grupos);
    const encabezado = [
      'Caseta', 'Cant. facturada', 'Importe facturado (c/IVA)', 'Cant. cruces',
      'Importe cruces', 'Diferencia', '% Diferencia', 'Estado', 'Revisado', 'Nota'
    ];
    const lineas = [encabezado.join(',')];
    for (const g of filas) {
      const estado = rowEstado(g);
      const pct = g.match && g.totalImporteFacturado > 0
        ? ((Math.abs(g.diferencia) / g.totalImporteFacturado) * 100).toFixed(2) + '%'
        : '';
      const rev = revisadas.get(g.key);
      lineas.push([
        csvEscape(g.casetaDisplay),
        csvEscape(g.totalCantidadFacturada),
        csvEscape(g.totalImporteFacturado.toFixed(2)),
        csvEscape(g.match ? g.match.totalCruces : ''),
        csvEscape(g.match ? g.match.totalImporte.toFixed(2) : ''),
        csvEscape(g.diferencia !== null ? g.diferencia.toFixed(2) : ''),
        csvEscape(pct),
        csvEscape(estado === 'sinMatch' ? 'Sin coincidencia' : estado === 'coincide' ? 'Coincide' : 'Diferencia'),
        csvEscape(rev ? 'Sí' : 'No'),
        csvEscape(rev?.nota || ''),
      ].join(','));
    }
    const csvContent = '﻿' + lineas.join('\n'); // BOM para acentos en Excel
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const hoy = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `conciliacion_${periodoInicio || hoy}_a_${periodoFin || hoy}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">Verificador de Facturas XML</h1>
      </div>

      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">Cargar CFDI (XML)</h6>
        </div>
        <div className="card-body">
          <div className="input-group mb-3">
            <input
              type="file"
              className="form-control"
              id="inputGroupFile02"
              accept=".xml"
              multiple
              onChange={handleFileUpload}
            />
            <label className="input-group-text" htmlFor="inputGroupFile02">
              <Upload size={18} className="mr-2" /> Cargar
            </label>
          </div>
          {facturas.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm mb-2">
                <thead className="thead-light">
                  <tr>
                    <th>Archivo</th>
                    <th>Serie-Folio</th>
                    <th>Fecha emisión</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Conceptos</th>
                    <th className="text-center" style={{ width: '5%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {facturas.map((f, i) => (
                    <tr key={f.uuid || `${f.fileName}-${i}`}>
                      <td>{f.fileName}</td>
                      <td>{[f.serie, f.folio].filter(Boolean).join('-') || '—'}</td>
                      <td>{new Date(f.fecha).toLocaleDateString()}</td>
                      <td className="text-right">{formatearDinero(f.total)}</td>
                      <td className="text-right">{f.conceptos.length}</td>
                      <td className="text-center">
                        <button
                          className="btn btn-outline-secondary btn-sm py-0"
                          title="Quitar factura"
                          onClick={() => removeFactura(i)}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <small className="text-muted">
                Puedes cargar más de un CFDI del mismo período (p. ej. la factura global del 1–30 y la del día 31);
                la conciliación compara la <strong>suma de todas</strong> contra los cruces del período.
              </small>
            </div>
          )}
          {error && (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <AlertCircle className="mr-2" />
              <div>{error}</div>
            </div>
          )}
        </div>
      </div>

      {facturas.length > 0 && (
        <>
          <div className="row">
            <div className="col-xl-4 col-md-6 mb-4">
              <div className="card border-left-primary shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                        {facturas.length === 1 ? 'Total Factura' : `Total de ${facturas.length} Facturas`}
                      </div>
                      <div className="h5 mb-0 font-weight-bold text-gray-800">
                        {formatearDinero(facturas.reduce((s, f) => s + f.total, 0))} {facturas[0].moneda}
                      </div>
                    </div>
                    <div className="col-auto"><DollarSign className="text-gray-300" size={32} /></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <div className="card border-left-success shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                        {facturas.length === 1 ? 'Fecha Emisión' : 'Rango de Emisión'}
                      </div>
                      {facturas.length === 1 ? (
                        <>
                          <div className="h5 mb-0 font-weight-bold text-gray-800">{new Date(facturas[0].fecha).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">{new Date(facturas[0].fecha).toLocaleTimeString()}</div>
                        </>
                      ) : (() => {
                        const fechas = facturas.map(f => new Date(f.fecha)).sort((a, b) => a - b);
                        return (
                          <>
                            <div className="h5 mb-0 font-weight-bold text-gray-800">
                              {fechas[0].toLocaleDateString()} – {fechas[fechas.length - 1].toLocaleDateString()}
                            </div>
                            <div className="text-xs text-gray-500">{facturas.length} CFDIs cargados</div>
                          </>
                        );
                      })()}
                    </div>
                    <div className="col-auto"><Calendar className="text-gray-300" size={32} /></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-4 col-md-6 mb-4">
              <div className="card border-left-info shadow h-100 py-2">
                <div className="card-body">
                  <div className="row no-gutters align-items-center">
                    <div className="col mr-2">
                      <div className="text-xs font-weight-bold text-info text-uppercase mb-1">Emisor</div>
                      <div className="font-weight-bold text-gray-800 " style={{ maxWidth: '150px' }} title={facturas[0].emisor.nombre}>{facturas[0].emisor.nombre}</div>
                      <div className="text-xs text-gray-500">{facturas[0].emisor.rfc}</div>
                    </div>
                    <div className="col-auto"><User className="text-gray-300" size={32} /></div>
                  </div>
                </div>
              </div>
            </div>


          </div>

          <div className="card shadow mb-4">
            <div className="card-header py-3 d-flex align-items-center justify-content-between">
              <h6 className="m-0 font-weight-bold text-primary">Conciliar con Cruces Registrados</h6>
            </div>
            <div className="card-body">
              <p className="text-muted small mb-3">
                Define el período de <strong>FechaAplicación</strong> en el que buscar los cruces.
                Se asignó automáticamente el mes de la factura; ajústalo si es necesario.
              </p>
              <div className="form-row align-items-end">
                <div className="form-group col-md-3">
                  <label className="small font-weight-bold">Período inicio</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={periodoInicio}
                    onChange={e => { setPeriodoInicio(e.target.value); setConciliacion(null); }}
                  />
                </div>
                <div className="form-group col-md-3">
                  <label className="small font-weight-bold">Período fin</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={periodoFin}
                    onChange={e => { setPeriodoFin(e.target.value); setConciliacion(null); }}
                  />
                </div>
                <div className="form-group col-md-3">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleConciliar}
                    ref={AtajoCalcular}
                    disabled={loadingConciliacion || !periodoInicio || !periodoFin}
                  >
                    {loadingConciliacion
                      ? <><span className="spinner-border spinner-border-sm mr-1" />Cargando...</>
                      : <><Search size={14} className="mr-1" />Conciliar</>
                    }
                  </button>
                </div>
              </div>
              {errorConciliacion && (
                <div className="alert alert-danger d-flex align-items-center mt-2" role="alert">
                  <AlertCircle size={16} className="mr-2" />
                  {errorConciliacion}
                </div>
              )}
            </div>
          </div>

          {conciliacion && (() => {
            const { grupos } = conciliacion;
            const gruposConManual = grupos.map(g => {
              const manual = obtenerConciliacionManualGrupo(g.key);
              return {
                ...g,
                totalCrucesVisual: (g.match?.totalCruces || 0) + manual.cantidad,
                totalImporteCrucesVisual: (g.match?.totalImporte || 0) + manual.importe,
                difCantidadVisual: g.difCantidad === null ? null : g.difCantidad + manual.cantidad,
                diferenciaVisual: g.diferencia === null ? null : g.diferencia - manual.importe,
              };
            }).sort((a, b) => {
              const aDiferencia = a.diferenciaVisual === null || Math.abs(a.diferenciaVisual) >= 0.01;
              const bDiferencia = b.diferenciaVisual === null || Math.abs(b.diferenciaVisual) >= 0.01;
              if (aDiferencia !== bDiferencia) return aDiferencia ? -1 : 1;
              return a.key.localeCompare(b.key);
            });
            const grupsFiltrados  = filtrarGrupos(gruposConManual);
            const totalFacturado  = grupsFiltrados.reduce((s, g) => s + g.totalImporteFacturado, 0);
            const totalCrucesSuma = grupsFiltrados.reduce((s, g) => s + (g.totalImporteCrucesVisual ?? 0), 0);
            const totalDif        = totalFacturado - totalCrucesSuma;
            const sinMatch        = gruposConManual.filter(g => !g.match).length;
            const conDif          = gruposConManual.filter(g => g.match && Math.abs(g.diferenciaVisual) >= 0.01).length;
            const coincide        = gruposConManual.length - sinMatch - conDif;

            const filtros = [
              { key: 'todas',      label: 'Todas',            count: grupos.length },
              { key: 'diferencia', label: 'Con diferencia',   count: conDif },
              { key: 'sinMatch',   label: 'Sin coincidencia', count: sinMatch },
              { key: 'coincide',   label: 'Coincide',         count: coincide },
            ];

            return (
              <div className="card shadow mb-4">
                <div className="card-header py-3 d-flex align-items-center justify-content-between flex-wrap">
                  <h6 className="m-0 font-weight-bold text-primary">
                    Resultado de Conciliación
                    <span className="text-muted font-weight-normal ml-2" style={{ fontSize: '0.8rem' }}>
                      — clic en una fila para ver el detalle por línea CFDI y clase de vehículo
                    </span>
                  </h6>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => exportarConciliacionCSV(gruposConManual)}
                    title="Exportar filas visibles a CSV"
                  >
                    ⬇ Exportar CSV
                  </button>
                </div>
                <div className="px-3 py-2 border-bottom d-flex align-items-center flex-wrap" style={{ gap: '0.4rem', background: '#fafbfc' }}>
                  <span className="text-muted small mr-1">Filtrar:</span>
                  {filtros.map(f => (
                    <button
                      key={f.key}
                      className={`btn btn-sm ${filtroEstado === f.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => setFiltroEstado(f.key)}
                    >
                      {f.label} <span className="badge badge-light border ml-1">{f.count}</span>
                    </button>
                  ))}
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive table-container" style={{ height: '70vh', maxHeight: '70vh' }}>
                    <table className="table table-scrollable table-bordered table-sm mb-0" width="100%" cellSpacing="0" style={{ fontSize: '0.95rem' }}>
                      <thead className="thead-light sticky-top">
                        <tr>
                          <th style={{ width: '20%' }}>Caseta / Descripción CFDI / Clase</th>
                          <th className="text-right" style={{ width: '9%' }}>Cant. fact.</th>
                          <th className="text-right" style={{ width: '12%' }}>Imp. fact. (c/IVA)</th>
                          <th className="text-right" style={{ width: '12%' }}>Tarifa / cruce (c/IVA)</th>
                          <th className="text-right" style={{ width: '9%' }}>Cant. cruces</th>
                          <th className="text-right" style={{ width: '12%' }}>Imp. cruces</th>
                          <th className="text-right" style={{ width: '10%' }}>Diferencia</th>
                          <th className="text-center" style={{ width: '8%' }}>Estado</th>
                          <th className="text-center" style={{ width: '8%' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupsFiltrados.length === 0 && (
                          <tr>
                            <td colSpan={9} className="text-center text-muted py-3">
                              No hay filas para el filtro seleccionado.
                            </td>
                          </tr>
                        )}
                        {grupsFiltrados.map((g) => {
                          const isExpanded = expandedGrupos.has(g.key);
                          const diferenciaFila = g.diferenciaVisual ?? g.diferencia;
                          const rowBg = !g.match
                            ? '#f8f9fa'
                            : Math.abs(diferenciaFila) < 0.01
                              ? ''
                              : (Math.abs(diferenciaFila) / g.totalImporteFacturado) * 100 < 1
                                ? '#fff8e1'
                                : '#fff5f5';
                          const detalle = emparejarDetalles(g.lineasCFDI, g.match?.clases || []);
                          const combinacionesGuardadas = combinacionesManuales.get(g.key) || [];
                          const lineasAsociadas = new Set(combinacionesGuardadas.flatMap(combinacion => combinacion.lineasCFDI.map(detalleItemKey)));
                          const clasesAsociadas = new Set(combinacionesGuardadas.flatMap(combinacion => combinacion.clasesIAVE.map(detalleItemKey)));
                          const lineasVinculadasFuera = detalle.lineasSinCoincidencia.filter(linea => conciliacionesManuales.has(`${g.key}::${linea.detalleId}`));
                          const lineasPendientes = detalle.lineasSinCoincidencia.filter(linea => (
                            !lineasAsociadas.has(detalleItemKey(linea))
                            && !conciliacionesManuales.has(`${g.key}::${linea.detalleId}`)
                          ));
                          const clasesPendientes = detalle.clasesSinCoincidencia.filter(clase => !clasesAsociadas.has(detalleItemKey(clase)));
                          const combinacionesDisponibles = buscarCombinacionesExactas(lineasPendientes, clasesPendientes);
                          return (
                            <React.Fragment key={g.key}>
                              {/* ── FILA RESUMEN (una por caseta) ── */}
                              <tr
                                onClick={() => toggleGrupo(g.key)}
                                style={{ cursor: 'pointer', background: rowBg, fontWeight: 600 }}
                              >
                                <td>
                                  <span className="mr-2" style={{ fontSize: '0.78rem', color: '#888' }}>
                                    {isExpanded ? '▼' : '▶'}
                                  </span>
                                  {g.casetaDisplay}
                                  {!g.match && (
                                    <span className="ml-2 text-muted small font-weight-normal">(sin match en cruces)</span>
                                  )}
                                </td>
                                <td className="text-right">{g.totalCantidadFacturada.toLocaleString('es-MX')}</td>
                                <td className="text-right">{formatearDinero(g.totalImporteFacturado)}</td>
                                <td className="text-right text-muted">—</td>
                                <td className="text-right">
                                  {g.match
                                    ? <>
                                        {(g.totalCrucesVisual ?? g.match.totalCruces).toLocaleString('es-MX')}
                                        {g.difCantidadVisual !== 0 && (
                                          <span className={`ml-1 small ${g.difCantidadVisual > 0 ? 'text-danger' : 'text-success'}`}>
                                            ({g.difCantidadVisual > 0 ? '+' : ''}{g.difCantidadVisual})
                                          </span>
                                        )}
                                      </>
                                    : <span className="text-muted">—</span>
                                  }
                                </td>
                                <td className="text-right">
                                  {g.match ? formatearDinero(g.totalImporteCrucesVisual ?? g.match.totalImporte) : <span className="text-muted">—</span>}
                                </td>
                                <td className="text-right">
                                  {diferenciaFila !== null
                                    ? <span className={diferenciaFila > 0.01 ? 'text-danger' : diferenciaFila < -0.01 ? 'text-success' : 'text-success'}>
                                        {diferenciaFila > 0 ? '+' : ''}{formatearDinero(diferenciaFila)}
                                      </span>
                                    : <span className="text-muted">—</span>
                                  }
                                </td>
                                <td className="text-center">{getEstadoBadge(diferenciaFila, g.totalImporteFacturado)}</td>
                                <td className="text-center" onClick={e => e.stopPropagation()}>
                                  {revisadas.has(g.key) ? (
                                    <span
                                      className="badge badge-success"
                                      style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                      title={`Nota: ${revisadas.get(g.key).nota || '(sin nota)'} · clic para quitar`}
                                      onClick={() => quitarRevisada(g.key)}
                                    >
                                      ✓ Revisado
                                    </span>
                                  ) : (
                                    <button
                                      className="btn btn-outline-secondary btn-sm py-0"
                                      style={{ fontSize: '0.75rem' }}
                                      title="Marcar como revisada/justificada"
                                      onClick={() => { setNotaEditKey(g.key); setNotaBorrador(''); }}
                                    >
                                      Revisar
                                    </button>
                                  )}
                                </td>
                              </tr>

                              {/* ── Editor de nota para marcar como revisada ── */}
                              {notaEditKey === g.key && (
                                <tr style={{ background: '#f7fdf9' }}>
                                  <td colSpan={9} className="py-2 px-3" onClick={e => e.stopPropagation()}>
                                    <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Nota u observación (opcional) — ej. 'Cambio de tarifa validado con PASE'"
                                        value={notaBorrador}
                                        onChange={e => setNotaBorrador(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') marcarRevisada(g.key, notaBorrador); if (e.key === 'Escape') setNotaEditKey(null); }}
                                        autoFocus
                                        style={{ maxWidth: '420px' }}
                                      />
                                      <button className="btn btn-success btn-sm" onClick={() => marcarRevisada(g.key, notaBorrador)}>Guardar</button>
                                      <button className="btn btn-outline-secondary btn-sm" onClick={() => setNotaEditKey(null)}>Cancelar</button>
                                    </div>
                                  </td>
                                </tr>
                              )}

                              {/* ── DETALLE (visible solo si expandido) ── */}
                              {isExpanded && (
                                <>
                                  {detalle.coincidencias.length > 0 && (
                                    <tr style={{ background: '#e6f4ea' }}>
                                      <td colSpan={9} className="py-1 px-3" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#287c3e', letterSpacing: '0.05em' }}>
                                        ↔ COINCIDENCIAS EXACTAS — CFDI e IAVE-WEB
                                      </td>
                                    </tr>
                                  )}
                                  {detalle.coincidencias.map(({ linea: l, clase: c }, index) => (
                                    <tr key={`coincidencia-${index}`} style={{ background: '#f4fbf5' }}>
                                      <td className="pl-4" style={{ fontSize: '0.9rem' }}>
                                        <div style={{ color: '#1a73e8', fontWeight: 600 }}>
                                          <span className="text-muted mr-1">└</span>{l.descripcion}
                                          {facturas.length > 1 && l.factura && (
                                            <span className="badge badge-light border ml-1" style={{ fontSize: '0.75rem' }}>{l.factura}</span>
                                          )}
                                        </div>
                                        <div className="text-secondary" style={{ fontSize: '0.82rem' }}>
                                          <span className="mr-1">└</span>Clase {c.Clase} en IAVE-WEB
                                        </div>
                                      </td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>
                                        <span style={{ color: '#1a73e8', fontWeight: 600 }}>{l.cantidad.toLocaleString('es-MX')}</span>
                                        <div className="text-secondary" style={{ fontSize: '0.82rem' }}>{c.TotalCruces.toLocaleString('es-MX')} cruces</div>
                                      </td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>
                                        <span style={{ color: '#1a73e8', fontWeight: 600 }}>{formatearDinero(l.importeConIVA)}</span>
                                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>s/IVA: {formatearDinero(l.importe)}</div>
                                      </td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>
                                        <span style={{ color: '#6c757d', fontWeight: 600 }}>{formatearDinero(c.tarifaPromedio)}<span className="font-weight-normal">/cruce</span></span>
                                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>registro IAVE-WEB</div>
                                      </td>
                                      <td className="text-right" style={{ color: '#6c757d', fontSize: '0.9rem' }}>{c.TotalCruces.toLocaleString('es-MX')}</td>
                                      <td className="text-right" style={{ color: '#6c757d', fontSize: '0.9rem' }}>{formatearDinero(c.TotalImporte)}</td>
                                      <td className="text-right text-success">$0.00</td>
                                      <td className="text-center"><span className="badge badge-success">Coincide</span></td>
                                      <td className="text-center text-muted">—</td>
                                    </tr>
                                  ))}

                                  {(combinacionesGuardadas.length > 0 || combinacionesDisponibles.length > 0) && (
                                    <>
                                      <tr style={{ background: '#e8f5e9' }}>
                                        <td colSpan={9} className="py-1 px-3" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#287c3e', letterSpacing: '0.05em' }}>
                                          ↔ COINCIDENCIAS POR COMBINACIÓN — CFDI e IAVE-WEB
                                        </td>
                                      </tr>
                                      {combinacionesGuardadas.map((combinacion, combinacionIndex) => (
                                        <tr key={`combinacion-guardada-${combinacionIndex}`} style={{ background: '#eaf7ed' }}>
                                          <td className="pl-4" style={{ fontSize: '0.9rem' }}>
                                            <div style={{ color: '#1a73e8', fontWeight: 600 }}>
                                              {combinacion.lineasCFDI.map(linea => linea.descripcion).join(' + ')}
                                            </div>
                                            <div className="text-secondary" style={{ fontSize: '0.82rem' }}>
                                              {combinacion.clasesIAVE.map(clase => `Clase ${clase.Clase}`).join(' + ')} en IAVE-WEB
                                            </div>
                                          </td>
                                          <td className="text-right" style={{ color: '#1a73e8' }}>{combinacion.cantidad.toLocaleString('es-MX')} cruces</td>
                                          <td className="text-right" style={{ color: '#1a73e8', fontWeight: 600 }}>{formatearDinero(combinacion.importe)}</td>
                                          <td className="text-right text-secondary">Asociado manualmente</td>
                                          <td className="text-right text-secondary">{combinacion.cantidad.toLocaleString('es-MX')}</td>
                                          <td className="text-right text-secondary">{formatearDinero(combinacion.importe)}</td>
                                          <td className="text-right text-success">$0.00</td>
                                          <td className="text-center"><span className="badge badge-info">Asociado</span></td>
                                          <td className="text-center" onClick={e => e.stopPropagation()}>
                                            <button className="btn btn-outline-danger btn-sm py-0" style={{ fontSize: '0.72rem' }} onClick={() => quitarCombinacionManual(g.key, combinacionIndex)}>
                                              Quitar
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                      {combinacionesDisponibles.map((combinacion, combinacionIndex) => (
                                        <tr key={`combinacion-disponible-${combinacionIndex}`} style={{ background: '#f4fbf5' }}>
                                          <td className="pl-4" style={{ fontSize: '0.9rem' }}>
                                            <div style={{ color: '#1a73e8', fontWeight: 600 }}>
                                              {combinacion.lineas.map(linea => linea.descripcion).join(' + ')}
                                            </div>
                                            <div className="text-secondary" style={{ fontSize: '0.82rem' }}>
                                              {combinacion.clases.map(clase => `Clase ${clase.Clase}`).join(' + ')} en IAVE-WEB
                                            </div>
                                          </td>
                                          <td className="text-right" style={{ color: '#1a73e8' }}>{combinacion.cantidad.toLocaleString('es-MX')} cruces</td>
                                          <td className="text-right" style={{ color: '#1a73e8', fontWeight: 600 }}>{formatearDinero(combinacion.importe)}</td>
                                          <td className="text-right text-secondary">Combinación exacta</td>
                                          <td className="text-right text-secondary">{combinacion.cantidad.toLocaleString('es-MX')}</td>
                                          <td className="text-right text-secondary">{formatearDinero(combinacion.importe)}</td>
                                          <td className="text-right text-success">$0.00</td>
                                          <td className="text-center"><span className="badge badge-warning">Pendiente</span></td>
                                          <td className="text-center" onClick={e => e.stopPropagation()}>
                                            <button className="btn btn-success btn-sm py-0" style={{ fontSize: '0.72rem' }} onClick={() => guardarCombinacionManual(g, combinacion)}>
                                              Asociar combinación
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </>
                                  )}

                                  {lineasVinculadasFuera.length > 0 && (
                                    <tr style={{ background: '#e8f5e9' }}>
                                      <td colSpan={9} className="py-1 px-3" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#287c3e', letterSpacing: '0.05em' }}>
                                        ↔ LÍNEAS DEL CFDI VINCULADAS CON CRUCES FUERA DEL RANGO
                                      </td>
                                    </tr>
                                  )}
                                  {lineasVinculadasFuera.map((l, li) => {
                                    const lineKey = `${g.key}::${l.detalleId}`;
                                    const vinculo = conciliacionesManuales.get(lineKey);
                                    return (
                                      <tr key={`vinculada-${li}`} style={{ background: '#f4fbf5' }}>
                                        <td className="pl-4" style={{ fontSize: '0.9rem' }}>
                                          <div style={{ color: '#1a73e8', fontWeight: 600 }}><span className="text-muted mr-1">└</span>{l.descripcion}</div>
                                          <div className="text-secondary" style={{ fontSize: '0.82rem' }}>
                                            <span className="mr-1">└</span>{vinculo?.candidato?.FechaInicio ? new Date(vinculo.candidato.FechaInicio).toLocaleDateString('es-MX') : 'Cruce fuera del rango'} · Clase {vinculo?.candidato?.Clase}
                                          </div>
                                        </td>
                                        <td className="text-right" style={{ color: '#1a73e8' }}>{l.cantidad.toLocaleString('es-MX')} cruces</td>
                                        <td className="text-right" style={{ color: '#1a73e8', fontWeight: 600 }}>{formatearDinero(l.importeConIVA)}</td>
                                        <td className="text-right text-secondary">{formatearDinero(vinculo?.candidato?.TotalImporte || 0)}<span className="font-weight-normal">/cruce total</span></td>
                                        <td className="text-right text-secondary">{Number(vinculo?.candidato?.TotalCruces || 0).toLocaleString('es-MX')}</td>
                                        <td className="text-right text-secondary">{formatearDinero(vinculo?.candidato?.TotalImporte || 0)}</td>
                                        <td className="text-right text-success">$0.00</td>
                                        <td className="text-center"><span className="badge badge-info">Vinculado</span></td>
                                        <td className="text-center" onClick={e => e.stopPropagation()}>
                                          <button className="btn btn-outline-danger btn-sm py-0" style={{ fontSize: '0.72rem' }} onClick={() => quitarConciliacionManual(lineKey)}>Quitar</button>
                                        </td>
                                      </tr>
                                    );
                                  })}

                                  {lineasPendientes.length > 0 && (
                                    <tr style={{ background: '#e8f4fd' }}>
                                      <td colSpan={9} className="py-1 px-3" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1a73e8', letterSpacing: '0.05em' }}>
                                        📄 LÍNEAS DEL CFDI SIN EMPAREJAR
                                      </td>
                                    </tr>
                                  )}
                                  {lineasPendientes.map((l, li) => {
                                    const lineKey = `${g.key}::${l.detalleId}`;
                                    const busqueda = busquedasManuales.get(lineKey);
                                    const vinculo = conciliacionesManuales.get(lineKey);
                                    return (
                                    <React.Fragment key={`cfdi-${li}`}>
                                    <tr style={{ background: '#f0f7ff' }}>
                                      <td className="pl-4" style={{ fontSize: '0.9rem' }}>
                                        <span className="text-muted mr-1">└</span>{l.descripcion}
                                        {facturas.length > 1 && l.factura && (
                                          <span className="badge badge-light border ml-1" style={{ fontSize: '0.75rem' }} title="CFDI de origen">{l.factura}</span>
                                        )}
                                        {l.claveProdServ && (
                                          <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>· SAT: {l.claveProdServ}</span>
                                        )}
                                      </td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>{l.cantidad.toLocaleString('es-MX')}</td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>
                                        {formatearDinero(l.importeConIVA)}
                                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>s/IVA: {formatearDinero(l.importe)}</div>
                                      </td>
                                      <td className="text-right text-primary" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                        {formatearDinero(l.valorUnitarioConIVA)}<span className="text-muted font-weight-normal">/cruce</span>
                                        <div className="text-muted font-weight-normal" style={{ fontSize: '0.78rem' }}>s/IVA: {formatearDinero(l.valorUnitario)}</div>
                                      </td>
                                      <td className="text-right text-muted">—</td>
                                      <td className="text-right text-muted">—</td>
                                      <td className="text-right text-muted">—</td>
                                      <td className="text-center text-muted">—</td>
                                      <td className="text-center" onClick={e => e.stopPropagation()}>
                                        {vinculo ? (
                                          <span
                                            className="badge badge-info"
                                            style={{ cursor: 'pointer', fontSize: '0.72rem' }}
                                            title="Quitar conciliación manual"
                                            onClick={() => quitarConciliacionManual(lineKey)}
                                          >
                                            ✓ Vinculado fuera del rango
                                          </span>
                                        ) : (
                                          <button
                                            className="btn btn-outline-primary btn-sm py-0"
                                            style={{ fontSize: '0.72rem' }}
                                            disabled={busqueda?.loading}
                                            onClick={() => buscarCrucesFueraPeriodo(lineKey, g, l)}
                                            title="Buscar cruces fuera del período conciliado"
                                          >
                                            {busqueda?.loading ? 'Buscando...' : 'Buscar fuera del rango'}
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                    {busqueda && !busqueda.loading && (
                                      <tr style={{ background: '#eef8ff' }} onClick={e => e.stopPropagation()}>
                                        <td colSpan={9} className="py-2 px-4">
                                          {busqueda.error ? (
                                            <span className="text-danger small">{busqueda.error}</span>
                                          ) : busqueda.candidatos.length === 0 ? (
                                            <span className="text-muted small">No se encontraron grupos exactos fuera del período conciliado.</span>
                                          ) : (
                                            <div>
                                              <div className="text-primary small font-weight-bold mb-1">Candidatos fuera del rango:</div>
                                              {busqueda.candidatos.map((candidato, candidatoIndex) => (
                                                <div key={`${lineKey}-candidato-${candidatoIndex}`} className="d-flex align-items-center flex-wrap mb-1" style={{ gap: '0.5rem' }}>
                                                  <span className="small">
                                                    {new Date(candidato.FechaInicio).toLocaleDateString('es-MX')}
                                                    {candidato.FechaInicio !== candidato.FechaFin && ` — ${new Date(candidato.FechaFin).toLocaleDateString('es-MX')}`}
                                                    {' · Clase '}{candidato.Clase}
                                                    {' · '}{candidato.TotalCruces} cruces
                                                    {' · '}{formatearDinero(candidato.TotalImporte)}
                                                  </span>
                                                  <button
                                                    className="btn btn-info btn-sm py-0"
                                                    style={{ fontSize: '0.72rem' }}
                                                    onClick={() => guardarConciliacionManual(lineKey, g, l, candidato)}
                                                  >
                                                    Asignar
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                    )}
                                    </React.Fragment>
                                    );
                                  })}

                                  {clasesPendientes.length > 0 && (
                                    <tr style={{ background: '#f0f0f0' }}>
                                      <td colSpan={9} className="py-1 px-3" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#555', letterSpacing: '0.05em' }}>
                                        🚛 CRUCES DEL PERÍODO SIN EMPAREJAR — registrados en IAVE-WEB
                                      </td>
                                    </tr>
                                  )}
                                  {clasesPendientes.map((c, ci) => (
                                    <tr key={`clase-${ci}`} style={{ background: '#fafafa' }}>
                                      <td className="pl-4" style={{ fontSize: '0.9rem' }}>
                                        <span className="text-muted mr-1">└</span>
                                        <span className="badge badge-light border mr-1">Clase {c.Clase}</span>
                                      </td>
                                      <td className="text-right text-muted">—</td>
                                      <td className="text-right text-muted">—</td>
                                      <td className="text-right text-secondary" style={{ fontSize: '0.9rem', fontWeight: 600 }}>
                                        {formatearDinero(c.tarifaPromedio)}<span className="text-muted font-weight-normal">/cruce</span>
                                      </td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>{c.TotalCruces.toLocaleString('es-MX')}</td>
                                      <td className="text-right" style={{ fontSize: '0.9rem' }}>{formatearDinero(c.TotalImporte)}</td>
                                      <td className="text-right text-muted">—</td>
                                      <td className="text-center text-muted">—</td>
                                      <td className="text-center text-muted">—</td>
                                    </tr>
                                  ))}
                                </>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                      <tfoot className="font-weight-bold">
                        <tr className="bg-light">
                          <td>
                            TOTALES
                            {filtroEstado !== 'todas' && (
                              <span className="text-muted font-weight-normal ml-1" style={{ fontSize: '0.78rem' }}>
                                (filtro: {filtros.find(f => f.key === filtroEstado)?.label})
                              </span>
                            )}
                          </td>
                          <td></td>
                          <td className="text-right">{formatearDinero(totalFacturado)}</td>
                          <td></td>
                          <td></td>
                          <td className="text-right">{formatearDinero(totalCrucesSuma)}</td>
                          <td className="text-right">
                            <span className={totalDif > 0.01 ? 'text-danger' : totalDif < -0.01 ? 'text-success' : 'text-success'}>
                              {totalDif > 0 ? '+' : ''}{formatearDinero(totalDif)}
                            </span>
                          </td>
                          <td></td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            );
          })()}

          {conciliacion && (() => {
            const { grupos, sinFactura = [] } = conciliacion;

            // Derivar qué está vinculado manualmente
            const linkedDbKeys   = new Set(manualLinks.keys());
            const linkedCfdiKeys = new Set(manualLinks.values());

            const sinFacturaVisible = sinFactura.filter(c => !linkedDbKeys.has(c._key));
            const sinCFDIVisible    = grupos.filter(g => !g.match && !linkedCfdiKeys.has(g.key));

            // Pares vinculados manualmente
            const linkedPairs = [];
            for (const [dbKey, cfdiKey] of manualLinks.entries()) {
              const dbCaseta  = sinFactura.find(c => c._key === dbKey);
              const cfdiGroup = grupos.find(g => g.key === cfdiKey);
              if (dbCaseta && cfdiGroup) {
                linkedPairs.push({
                  dbCaseta,
                  cfdiGroup,
                  diferencia:  cfdiGroup.totalImporteFacturado - dbCaseta.totalImporte,
                  difCantidad: cfdiGroup.totalCantidadFacturada - dbCaseta.totalCruces,
                });
              }
            }

            return (
              <>
                {/* ── CFDI sin coincidencia en cruces ── */}
                {sinCFDIVisible.length > 0 && (
                  <div className="card shadow mb-4">
                    <div className="card-header py-3 d-flex align-items-center justify-content-between" style={{ borderLeft: '4px solid #e74a3b' }}>
                      <h6 className="m-0 font-weight-bold text-danger">
                        ❌ Conceptos del CFDI sin coincidencia en cruces registrados
                      </h6>
                      <span className="badge badge-danger">{sinCFDIVisible.length} concepto{sinCFDIVisible.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0" width="100%" cellSpacing="0" style={{ fontSize: '0.95rem' }}>
                          <thead className="thead-light">
                            <tr>
                              <th style={{ width: '35%' }}>Descripción en CFDI</th>
                              <th className="text-right" style={{ width: '12%' }}>Cant.</th>
                              <th className="text-right" style={{ width: '18%' }}>Importe (c/IVA)</th>
                              <th style={{ width: '35%' }}>¿Corresponde a caseta del sistema?</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sinCFDIVisible.map((g) => (
                              <tr key={g.key} style={{ background: '#fff5f5' }}>
                                <td className="font-weight-bold">
                                  {g.lineasCFDI.length > 1
                                    ? <>{g.casetaDisplay} <span className="badge badge-light border">{g.lineasCFDI.length} líneas</span></>
                                    : g.lineasCFDI[0]?.descripcion
                                  }
                                </td>
                                <td className="text-right">{g.totalCantidadFacturada.toLocaleString('es-MX')}</td>
                                <td className="text-right font-weight-bold text-danger">{formatearDinero(g.totalImporteFacturado)}</td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <select
                                      className="form-control form-control-sm mr-2"
                                      defaultValue=""
                                      onChange={e => {
                                        // Vinculación desde el lado CFDI: invertimos el link (dbKey → cfdiKey)
                                        if (e.target.value) handleLink(e.target.value, g.key);
                                      }}
                                    >
                                      <option value="">— selecciona caseta del sistema —</option>
                                      {sinFacturaVisible.map(sf => (
                                        <option key={sf._key} value={sf._key}>
                                          {sf.Caseta} ({sf.totalCruces} cruces · {formatearDinero(sf.totalImporte)})
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Cruces sin coincidencia en el CFDI ── */}
                {sinFacturaVisible.length > 0 && (
                  <div className="card shadow mb-4">
                    <div className="card-header py-3 d-flex align-items-center justify-content-between" style={{ borderLeft: '4px solid #f6c23e' }}>
                      <h6 className="m-0 font-weight-bold text-warning">
                        ⚠️ Cruces en el período sin coincidencia en el CFDI
                      </h6>
                      <div>
                        <span className="badge badge-warning mr-2">{sinFacturaVisible.length} caseta{sinFacturaVisible.length > 1 ? 's' : ''}</span>
                        <span className="badge badge-secondary">
                          {sinFacturaVisible.reduce((s, c) => s + c.totalCruces, 0).toLocaleString('es-MX')} cruces
                        </span>
                      </div>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0" width="100%" cellSpacing="0" style={{ fontSize: '0.95rem' }}>
                          <thead className="thead-light">
                            <tr>
                              <th style={{ width: '22%' }}>Caseta (en sistema)</th>
                              <th className="text-right" style={{ width: '8%' }}>Cruces</th>
                              <th className="text-right" style={{ width: '13%' }}>Total cobrado</th>
                              <th className="text-right" style={{ width: '13%' }}>Tarifa prom.</th>
                              <th style={{ width: '17%' }}>Clases</th>
                              <th style={{ width: '27%' }}>¿Corresponde a concepto del CFDI?</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sinFacturaVisible.map((c, i) => (
                              <tr key={i} style={{ background: '#fffbf0' }}>
                                <td className="font-weight-bold">{c.Caseta}</td>
                                <td className="text-right">{c.totalCruces.toLocaleString('es-MX')}</td>
                                <td className="text-right font-weight-bold text-warning">{formatearDinero(c.totalImporte)}</td>
                                <td className="text-right">
                                  {c.totalCruces > 0 ? formatearDinero(c.totalImporte / c.totalCruces) : '—'}
                                  <span className="text-muted">/cruce</span>
                                </td>
                                <td>
                                  {c.clases.map((cl, ci) => (
                                    <span key={ci} className="badge badge-light border mr-1 mb-1" style={{ fontSize: '0.8rem' }}>
                                      {cl.Clase}: {cl.TotalCruces}
                                    </span>
                                  ))}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <select
                                      className="form-control form-control-sm mr-2"
                                      value={manualLinks.get(c._key) || ''}
                                      onChange={e => handleLink(c._key, e.target.value || null)}
                                    >
                                      <option value="">— selecciona concepto del CFDI —</option>
                                      {sinCFDIVisible.map(g => (
                                        <option key={g.key} value={g.key}>
                                          {g.casetaDisplay} ({g.totalCantidadFacturada} · {formatearDinero(g.totalImporteFacturado)})
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="font-weight-bold bg-light">
                            <tr>
                              <td>TOTAL</td>
                              <td className="text-right">{sinFacturaVisible.reduce((s, c) => s + c.totalCruces, 0).toLocaleString('es-MX')}</td>
                              <td className="text-right text-warning">{formatearDinero(sinFacturaVisible.reduce((s, c) => s + c.totalImporte, 0))}</td>
                              <td colSpan={3}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className="px-3 py-2 border-top" style={{ background: '#fffdf0' }}>
                        <small className="text-muted">
                          Casetas con cruces en el período que no aparecen en el CFDI. Usa el selector para vincularlas manualmente a un concepto de la factura.
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Vínculos manuales ── */}
                {linkedPairs.length > 0 && (
                  <div className="card shadow mb-4">
                    <div className="card-header py-3 d-flex align-items-center justify-content-between" style={{ borderLeft: '4px solid #36b9cc' }}>
                      <h6 className="m-0 font-weight-bold text-info">
                        🔗 Vínculos manuales (conciliación temporal)
                      </h6>
                      <span className="badge badge-info">{linkedPairs.length} vínculo{linkedPairs.length > 1 ? 's' : ''}</span>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-bordered table-sm mb-0" width="100%" cellSpacing="0" style={{ fontSize: '0.95rem' }}>
                          <thead className="thead-light">
                            <tr>
                              <th style={{ width: '22%' }}>Concepto CFDI</th>
                              <th style={{ width: '22%' }}>Caseta en sistema</th>
                              <th className="text-right" style={{ width: '13%' }}>Imp. facturado (c/IVA)</th>
                              <th className="text-right" style={{ width: '13%' }}>Imp. en cruces</th>
                              <th className="text-right" style={{ width: '12%' }}>Diferencia</th>
                              <th className="text-center" style={{ width: '10%' }}>Estado</th>
                              <th className="text-center" style={{ width: '8%' }}>Acción</th>
                            </tr>
                          </thead>
                          <tbody>
                            {linkedPairs.map(({ dbCaseta, cfdiGroup, diferencia, difCantidad }, i) => (
                              <tr key={i} style={{ background: '#f0fbff' }}>
                                <td>
                                  <span className="font-weight-bold">{cfdiGroup.casetaDisplay}</span>
                                  <div className="text-muted" style={{ fontSize: '0.82rem' }}>{cfdiGroup.totalCantidadFacturada} cruces fact.</div>
                                </td>
                                <td>
                                  <span className="font-weight-bold">{dbCaseta.Caseta}</span>
                                  <div className="text-muted" style={{ fontSize: '0.82rem' }}>{dbCaseta.totalCruces} cruces en sistema</div>
                                </td>
                                <td className="text-right">{formatearDinero(cfdiGroup.totalImporteFacturado)}</td>
                                <td className="text-right">{formatearDinero(dbCaseta.totalImporte)}</td>
                                <td className="text-right font-weight-bold">
                                  <span className={diferencia > 0.01 ? 'text-danger' : diferencia < -0.01 ? 'text-success' : 'text-success'}>
                                    {diferencia > 0 ? '+' : ''}{formatearDinero(diferencia)}
                                  </span>
                                </td>
                                <td className="text-center">{getEstadoBadge(diferencia, cfdiGroup.totalImporteFacturado)}</td>
                                <td className="text-center">
                                  <button
                                    className="btn btn-outline-secondary btn-sm"
                                    title="Deshacer vínculo"
                                    onClick={() => handleLink(dbCaseta._key, null)}
                                  >
                                    ✕
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="font-weight-bold bg-light">
                            <tr>
                              <td colSpan={2}>TOTAL VINCULADO</td>
                              <td className="text-right">{formatearDinero(linkedPairs.reduce((s, p) => s + p.cfdiGroup.totalImporteFacturado, 0))}</td>
                              <td className="text-right">{formatearDinero(linkedPairs.reduce((s, p) => s + p.dbCaseta.totalImporte, 0))}</td>
                              <td className="text-right">
                                {(() => {
                                  const dif = linkedPairs.reduce((s, p) => s + p.diferencia, 0);
                                  return <span className={dif > 0.01 ? 'text-danger' : 'text-success'}>{dif > 0 ? '+' : ''}{formatearDinero(dif)}</span>;
                                })()}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()}

          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                {facturas.length === 1 ? 'Conceptos de la Factura' : 'Conceptos de las Facturas'}
              </h6>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div className="table-container">
                  <table className="table table-bordered table-striped" width="100%" cellSpacing="0">
                    <thead className="thead-light">
                      <tr>
                        {facturas.length > 1 && <th>Factura</th>}
                        <th>Cant.</th>
                        <th>Unidad</th>
                        <th>Descripción</th>
                        <th className="text-right">Precio Unit. sin IVA</th>
                        <th className="text-right">Subtotal sin IVA</th>
                        <th className="text-right">IVA</th>
                        <th className="text-right">Importe final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facturas.flatMap((f, fi) => {
                        const etiqueta = [f.serie, f.folio].filter(Boolean).join('-') || f.fileName;
                        return f.conceptos.map((concepto, idx) => (
                          <tr key={`${fi}-${idx}`}>
                            {facturas.length > 1 && (
                              <td><span className="badge badge-light border">{etiqueta}</span></td>
                            )}
                            <td>{concepto.cantidad}</td>
                            <td>{concepto.unidad}</td>
                            <td>
                              {concepto.descripcion}
                              <br />
                              <small className="text-muted">Clave SAT: {concepto.claveProdServ}</small>
                            </td>
                            <td className="text-right">{formatearDinero(concepto.valorUnitario)}</td>
                            <td className="text-right">{formatearDinero(concepto.importe)}</td>
                            <td className="text-right">{formatearDinero(concepto.importe * 0.16)}</td>
                            <td className="text-right">{formatearDinero(concepto.importe * 1.16)}</td>
                          </tr>
                        ));
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={facturas.length > 1 ? 6 : 5} className="text-right font-weight-bold">Subtotal:</td>
                        <td colSpan="2" className="text-right font-weight-bold">
                          {formatearDinero(facturas.reduce((s, f) => s + f.subTotal, 0))}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={facturas.length > 1 ? 6 : 5} className="text-right font-weight-bold">Total:</td>
                        <td colSpan="2" className="text-right font-weight-bold text-primary">
                          {formatearDinero(facturas.reduce((s, f) => s + f.total, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default XmlAnalyzer;